import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_PROFILE_KEY,
  normalizePermissionEmail,
  toPersistedProfile,
} from "../../utils/resolveUserPermissions";
import { createDefaultPermissionProfile } from "../../config/permissionDefaults";
import { PERMISSION_SECTIONS } from "../../config/permissionSchema";
import {
  fetchPermissionsRegistry,
  updatePermissionsRegistry,
} from "../../services/permissionsService";
import {
  formatPermissionsChangeCount,
  getPermissionsChangeCount,
  isActionFlagModified,
  isDefaultProfileKey,
  isEmailModified,
  isFeatureFlagModified,
} from "../../utils/permissionsChangeUtils";
import { useUI } from "../Context/UIContext";

const cloneRegistry = (registry) => JSON.parse(JSON.stringify(registry ?? {}));

const cloneProfile = (profile) => JSON.parse(JSON.stringify(profile ?? createDefaultPermissionProfile()));

const ensureProfileShape = (profile) => {
  const base = createDefaultPermissionProfile();
  const next = cloneProfile(base);
  Object.entries(profile?.featureFlags ?? {}).forEach(([key, value]) => {
    if (typeof value === "boolean" && key in next.featureFlags) {
      next.featureFlags[key] = value;
    }
  });
  const sourceActions = profile?.actionFlags ?? profile?.tableActions ?? {};
  Object.entries(sourceActions).forEach(([group, actions]) => {
    if (!next.actionFlags[group] || typeof actions !== "object") {
      return;
    }
    Object.entries(actions).forEach(([action, value]) => {
      if (typeof value === "boolean" && action in next.actionFlags[group]) {
        next.actionFlags[group][action] = value;
      }
    });
  });
  return next;
};

const sanitizeRegistry = (registry) => Object.fromEntries(
  Object.entries(registry ?? {}).map(([key, profile]) => [key, ensureProfileShape(profile)])
);

const modifiedFieldSx = {
  borderRadius: 1,
  px: 1,
  mx: -1,
  bgcolor: "warning.light",
  color: "warning.contrastText",
  "& .MuiFormControlLabel-label": {
    fontWeight: 600,
  },
};

const nestedItemSx = {
  pl: { xs: 1, md: 2 },
  borderLeft: "2px solid #e2e8f0",
};

const extractSaveErrorMessage = (error) => {
  const backendMessage = error?.response?.data?.message;
  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return backendMessage;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Salvataggio permessi non riuscito.";
};

const PermissionsSettings = () => {
  const { showError, showSuccess, showLoading, hideOverlay } = useUI();
  const [registry, setRegistry] = useState({});
  const [baselineRegistry, setBaselineRegistry] = useState({});
  const [selectedKey, setSelectedKey] = useState(DEFAULT_PROFILE_KEY);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchPermissionsRegistry({ forceRefresh: true });
      const normalized = sanitizeRegistry(cloneRegistry(payload));
      if (!normalized[DEFAULT_PROFILE_KEY]) {
        normalized[DEFAULT_PROFILE_KEY] = createDefaultPermissionProfile();
      }
      setRegistry(normalized);
      setBaselineRegistry(cloneRegistry(normalized));
      setSelectedKey((current) => (normalized[current] ? current : DEFAULT_PROFILE_KEY));
    } catch (error) {
      showError(error instanceof Error ? error.message : "Impossibile caricare i permessi.");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadRegistry();
  }, [loadRegistry]);

  const sortedKeys = useMemo(() => {
    const keys = Object.keys(registry);
    return keys
      .filter((key) => !search.trim() || key.toLowerCase().includes(search.trim().toLowerCase()))
      .sort((a, b) => {
        if (a === DEFAULT_PROFILE_KEY) return -1;
        if (b === DEFAULT_PROFILE_KEY) return 1;
        return a.localeCompare(b);
      });
  }, [registry, search]);

  const changeCount = useMemo(
    () => getPermissionsChangeCount(baselineRegistry, registry),
    [baselineRegistry, registry]
  );

  const dirty = changeCount > 0;
  const selectedProfile = registry[selectedKey] ?? createDefaultPermissionProfile();

  const updateSelectedProfile = (updater) => {
    setRegistry((current) => ({
      ...current,
      [selectedKey]: updater(ensureProfileShape(current[selectedKey])),
    }));
  };

  const handleFeatureFlagChange = (key, checked) => {
    updateSelectedProfile((profile) => ({
      ...profile,
      featureFlags: { ...profile.featureFlags, [key]: checked },
    }));
  };

  const handleActionFlagChange = (group, action, checked) => {
    updateSelectedProfile((profile) => ({
      ...profile,
      actionFlags: {
        ...profile.actionFlags,
        [group]: {
          ...profile.actionFlags[group],
          [action]: checked,
        },
      },
    }));
  };

  const handleAddProfile = () => {
    const email = normalizePermissionEmail(newEmail);
    if (!email) {
      showError("Inserisci un indirizzo email valido.");
      return;
    }
    if (registry[email]) {
      showError("Profilo già presente.");
      return;
    }
    const template = registry[DEFAULT_PROFILE_KEY] ?? createDefaultPermissionProfile();
    setRegistry((current) => ({
      ...current,
      [email]: cloneProfile(template),
    }));
    setSelectedKey(email);
    setNewEmail("");
    setAddDialogOpen(false);
  };

  const handleDeleteProfile = () => {
    if (selectedKey === DEFAULT_PROFILE_KEY) {
      return;
    }
    setRegistry((current) => {
      const next = { ...current };
      delete next[selectedKey];
      return next;
    });
    setSelectedKey(DEFAULT_PROFILE_KEY);
  };

  const handleReload = () => {
    if (dirty && !window.confirm("Ci sono modifiche non salvate. Ricaricare?")) {
      return;
    }
    loadRegistry();
  };

  const handleSave = async () => {
    const payload = Object.fromEntries(
      Object.entries(sanitizeRegistry(registry)).map(([key, profile]) => [key, toPersistedProfile(profile)])
    );
    if (!payload[DEFAULT_PROFILE_KEY]) {
      showError('Il profilo "default" è obbligatorio.');
      return;
    }
    setSaving(true);
    showLoading("Salvataggio permessi...");
    try {
      const saved = await updatePermissionsRegistry(payload);
      const normalized = sanitizeRegistry(cloneRegistry(saved));
      if (!normalized[DEFAULT_PROFILE_KEY]) {
        normalized[DEFAULT_PROFILE_KEY] = createDefaultPermissionProfile();
      }
      setRegistry(normalized);
      setBaselineRegistry(cloneRegistry(normalized));
      showSuccess("Permessi salvati.");
    } catch (error) {
      showError(extractSaveErrorMessage(error));
    } finally {
      hideOverlay();
      setSaving(false);
    }
  };

  const renderPermissionItem = (item) => {
    const featureModified = isFeatureFlagModified(baselineRegistry, registry, selectedKey, item.featureFlag);
    const isNested = Boolean(item.nestedUnder);

    return (
      <Box key={item.featureFlag} sx={isNested ? nestedItemSx : undefined} mb={2}>
        <FormControlLabel
          sx={featureModified ? modifiedFieldSx : undefined}
          control={(
            <Switch
              checked={selectedProfile.featureFlags?.[item.featureFlag] === true}
              onChange={(event) => handleFeatureFlagChange(item.featureFlag, event.target.checked)}
            />
          )}
          label={(
            <Typography component="span" fontWeight={700}>
              {item.label}
            </Typography>
          )}
        />

        {item.actionGroups?.map((group) => (
          <Box key={group.id} sx={{ pl: 3, mt: 0.5 }}>
            {group.label ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {group.label}
              </Typography>
            ) : null}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                gap: 0.5,
              }}
            >
              {group.actions.map(({ key, label }) => {
                const modified = isActionFlagModified(
                  baselineRegistry,
                  registry,
                  selectedKey,
                  group.id,
                  key
                );
                return (
                  <FormControlLabel
                    key={`${group.id}-${key}`}
                    sx={modified ? modifiedFieldSx : undefined}
                    control={(
                      <Switch
                        size="small"
                        checked={selectedProfile.actionFlags?.[group.id]?.[key] === true}
                        onChange={(event) => handleActionFlagChange(group.id, key, event.target.checked)}
                      />
                    )}
                    label={label}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return <Typography color="text.secondary">Caricamento permessi...</Typography>;
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Button variant="contained" disabled={!dirty || saving} onClick={handleSave}>
          Salva modifiche
        </Button>
        <Button variant="outlined" onClick={handleReload} disabled={saving}>
          Ricarica
        </Button>
        <Button variant="outlined" onClick={() => setAddDialogOpen(true)}>
          Aggiungi profilo
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={selectedKey === DEFAULT_PROFILE_KEY}
          onClick={handleDeleteProfile}
        >
          Elimina profilo
        </Button>
        <Chip
          label={formatPermissionsChangeCount(changeCount)}
          color={dirty ? "warning" : "default"}
          variant={dirty ? "filled" : "outlined"}
        />
      </Stack>

      {dirty ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {formatPermissionsChangeCount(changeCount)}.
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
          <TextField
            fullWidth
            size="small"
            label="Cerca email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Stack spacing={0.5}>
            {sortedKeys.map((key) => {
              const modified = isEmailModified(baselineRegistry, registry, key);
              return (
                <Button
                  key={key}
                  variant={key === selectedKey ? "contained" : "text"}
                  onClick={() => setSelectedKey(key)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontWeight: modified ? 700 : 400,
                    bgcolor: modified && key !== selectedKey ? "warning.light" : undefined,
                    color: modified && key !== selectedKey ? "warning.contrastText" : undefined,
                  }}
                >
                  {isDefaultProfileKey(key) ? `${key} (fallback)` : key}
                </Button>
              );
            })}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedKey === DEFAULT_PROFILE_KEY
              ? "Profilo default (email non configurate)"
              : selectedKey}
          </Typography>

          {PERMISSION_SECTIONS.map((section) => (
            <Box key={section.id} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1.5, fontWeight: 700, color: "#0b3d87", textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                {section.title}
              </Typography>
              {section.items.map((item) => renderPermissionItem(item))}
            </Box>
          ))}
        </Box>
      </Stack>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nuovo profilo permessi</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Email admin"
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleAddProfile}>Aggiungi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionsSettings;
